/**
 * Leitura LITERAL de `ultima_falha_em` — o carimbo da última falha do par
 * (empresa, bot), que o backend manda como "2026-09-14T04:49:22": horário de São
 * Paulo, sem fuso e sem "Z".
 *
 * Mesmo padrão de `formatarTsAuditoria` (utils/auditoriaErros): os campos saem da
 * string por regex, NUNCA de `new Date()`. Passar por `Date` reinterpreta o
 * horário no fuso de quem está olhando, e uma falha das 23:30 viraria o dia
 * seguinte para quem abre a tela fora de São Paulo — o popup diria "falhou em
 * 15/09" para uma falha que a auditoria lista em 14/09.
 *
 * Formato inesperado, `null`, `undefined` e string vazia devolvem `null`, sem
 * exceção: a tela cai no comportamento sem data (backend antigo ou resposta em
 * cache mandam o payload SEM o campo).
 */
export interface IDataFalha {
  /** "dd/mm" — o que cabe no selo e na caixa da pendente. */
  dia: string;
  /** "hh:mm" — só o tooltip usa. */
  hora: string;
}

export const lerDataFalha = (ts: string | null | undefined): IDataFalha | null => {
  const m = (ts ?? '').match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return null;
  return { dia: `${m[3]}/${m[2]}`, hora: `${m[4]}:${m[5]}` };
};

/** "dd/mm" da última falha, ou `null` quando não há data legível. */
export const diaDaFalha = (ts: string | null | undefined): string | null =>
  lerDataFalha(ts)?.dia ?? null;

/**
 * "dd/MM/AA HH:mm:ss" — o formato que o dono pediu para a linha do tempo do
 * popup (05/10). MESMA leitura literal por regex de `lerDataFalha`, pelo mesmo
 * motivo: o backend manda horário de São Paulo sem fuso, e passar por `Date`
 * reinterpretaria no fuso de quem olha.
 *
 * O ano vem com DOIS dígitos porque é o que o dono escreveu e é o que cabe na
 * linha; os segundos entram porque na linha do tempo duas tentativas do mesmo
 * minuto são eventos distintos — sem eles duas linhas pareceriam a mesma.
 *
 * Segundo ausente na string vira "00": o formato curto ("2026-10-05T09:41") é
 * data legível, e recusá-la devolveria `null` para um carimbo que existe.
 */
export const dataHoraCompleta = (ts: string | null | undefined): string | null => {
  const m = (ts ?? '').match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  return `${m[3]}/${m[2]}/${m[1].slice(2)} ${m[4]}:${m[5]}:${m[6] ?? '00'}`;
};
